/**
 * First-person view. Purely presentational: it reads interpolated simulation
 * state and never writes back. Effects (roll) are optional and subtle.
 */
import * as THREE from 'three';
import type { Vec3Like } from '../core/Vec3';

export interface CameraSettings {
  fovDeg: number;
  /** Max roll in degrees from lateral velocity; 0 disables. */
  rollDeg: number;
}

export class FirstPersonCamera {
  readonly camera: THREE.PerspectiveCamera;
  private roll = 0;
  private readonly euler = new THREE.Euler(0, 0, 0, 'YXZ');

  constructor(public settings: CameraSettings) {
    this.camera = new THREE.PerspectiveCamera(settings.fovDeg, 1, 0.05, 1200);
  }

  setFov(fovDeg: number): void {
    this.settings.fovDeg = fovDeg;
    this.camera.fov = fovDeg;
    this.camera.updateProjectionMatrix();
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  /**
   * @param eye     interpolated eye position
   * @param velocity current velocity (for optional roll)
   * @param dtFrame seconds since last frame (roll smoothing only)
   */
  update(eye: Readonly<Vec3Like>, yaw: number, pitch: number, velocity: Readonly<Vec3Like>, dtFrame: number): void {
    let targetRoll = 0;
    if (this.settings.rollDeg > 0) {
      // Lateral velocity relative to the view (right vector = (cos yaw, 0, -sin yaw)).
      const lateral = velocity.x * Math.cos(yaw) - velocity.z * Math.sin(yaw);
      const max = (this.settings.rollDeg * Math.PI) / 180;
      targetRoll = -Math.max(-1, Math.min(1, lateral / 20)) * max;
    }
    this.roll += (targetRoll - this.roll) * (1 - Math.exp(-10 * Math.min(dtFrame, 0.1)));
    this.camera.position.set(eye.x, eye.y, eye.z);
    this.euler.set(pitch, yaw, this.roll, 'YXZ');
    this.camera.quaternion.setFromEuler(this.euler);
  }
}
